import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import MainLayout from '../../layouts/MainLayout';
import { Card, Button, Space, message, Form } from 'antd';
import { SaveOutlined, ArrowRightOutlined, TeamOutlined } from '@ant-design/icons';
import ClientForm, { buildClientFormData } from '../../components/clients/ClientForm';
import { clientsApi, show422Error, ValidationError } from '../../utils/api';
import { resolveClientAvatarUrl, resolveClientLogoUrl } from '../../utils/clientUtils';
import { FormSkeleton } from '../../components/SkeletonLoaders';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';

const EditClient = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [sources, setSources] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [existingImageUrl, setExistingImageUrl] = useState(null);
  const [existingLogoUrl, setExistingLogoUrl] = useState(null);

  const loadEditForm = async () => {
    const res = await clientsApi.getEditForm(id);
    const data = res?.data ?? res;
    const client = data?.client;
    const d = client?.client_details || {};
    setCategories(data?.categories || []);
    setSubcategories(data?.subcategories || data?.subCategories || []);
    setSources(data?.sources || []);
    setEmployees(data?.employees || []);
    setExistingImageUrl(resolveClientAvatarUrl(client));
    setExistingLogoUrl(resolveClientLogoUrl(client, d));

    form.setFieldsValue({
          name: client?.name,
          email: client?.email,
          mobile: client?.mobile,
          gender: client?.gender || 'male',
          status: client?.status || 'active',
          login: client?.login || 'disable',
          sendMail: 'yes',
          category_id: d?.category_id,
          sub_category_id: d?.sub_category_id,
          source_id: client?.source_id ?? client?.source?.id ?? d?.source_id,
          added_by: d?.added_by,
          company_name: d?.company_name,
          website: d?.website,
          office: d?.office,
          city: d?.city,
          state: d?.state,
          postal_code: d?.postal_code,
          address: d?.address,
          shipping_address: d?.shipping_address,
          note: d?.note,
        });
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        await loadEditForm();
      } catch (e) {
        console.error(e);
        message.error('خطا در دریافت اطلاعات مشتری');
        navigate('/clients');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, navigate, form]);

  const refreshTaxonomyOnly = async () => {
    try {
      const res = await clientsApi.getEditForm(id);
      const data = res?.data ?? res;
      setCategories(data?.categories || []);
      setSubcategories(data?.subcategories || data?.subCategories || []);
      setSources(data?.sources || []);
    } catch (e) {
      console.error(e);
    }
  };

  const handleSubmit = async (values, fileOpts) => {
    setSubmitting(true);
    try {
      const fd = buildClientFormData(values, { ...fileOpts, isEdit: true });
      await clientsApi.update(id, fd);
      message.success('تغییرات ذخیره شد');
      navigate(`/clients/${id}`);
    } catch (error) {
      if (error instanceof ValidationError || error?.error?.details) {
        show422Error(error, form);
      } else {
        message.error(error?.message || 'خطا در ذخیره');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MainLayout>
      <div className="custom-font" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center">
                <TeamOutlined className="text-amber-600 dark:text-amber-400 text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0">ویرایش مشتری</h1>
                <p className="text-gray-500 dark:text-gray-400 text-sm m-0">شناسه: {id}</p>
              </div>
            </div>
            <Space>
              <Button icon={<ArrowRightOutlined />} onClick={() => navigate(`/clients/${id}`)}>
                بازگشت
              </Button>
              {/* <Button type="primary" icon={<SaveOutlined />} disabled className={PANEL_PRIMARY_BTN_CLASS}>
                راهنما
              </Button> */}
            </Space>
          </div>

          <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
            {loading ? (
              <FormSkeleton />
            ) : (
              <Card className="shadow-sm border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 custom-font">
                <ClientForm
                  form={form}
                  loading={false}
                  submitting={submitting}
                  isEdit
                  categories={categories}
                  subcategories={subcategories}
                  sources={sources}
                  employees={employees}
                  existingImageUrl={existingImageUrl}
                  existingLogoUrl={existingLogoUrl}
                  onSubmit={handleSubmit}
                  onCancel={() => navigate(`/clients/${id}`)}
                  onRefreshTaxonomy={refreshTaxonomyOnly}
                  excludeClientId={id}
                />
              </Card>
            )}
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default EditClient;
